import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  alpha
} from '@mui/material';
import {
  Checklist,
  Settings,
  FilterList,
  Assessment,
  Description,
  Tune,
  Router,
  Terminal
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useIsAdmin } from '@stores/userStore';
import { cloneElement, type ReactElement } from 'react';

type AdminTool = {
  id: string;
  titleKey: string;
  descriptionKey: string;
  icon: ReactElement;
  path: string;
  available: boolean;
};

export const AdministrationDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>
          {t('settings:administration.accessDenied.message')}
        </Typography>
      </Box>
    );
  }

  const adminTools: AdminTool[] = [
    {
      id: 'precal-checklist',
      titleKey: 'settings:administration.tools.precalChecklist.title',
      descriptionKey:
        'settings:administration.tools.precalChecklist.description',
      icon: <Checklist />,
      path: '/settings/administration/precal-checklist',
      available: true
    },
    {
      id: 'allowed-events',
      titleKey: 'settings:administration.tools.allowedEvents.title',
      descriptionKey: 'settings:administration.tools.allowedEvents.description',
      icon: <FilterList />,
      path: '/settings/administration/allowed-events',
      available: true
    },
    {
      id: 'equipment-assessment',
      titleKey: 'settings:administration.tools.equipmentAssessment.title',
      descriptionKey:
        'settings:administration.tools.equipmentAssessment.description',
      icon: <Assessment />,
      path: '/settings/administration/equipment-assessment',
      available: true
    },
    {
      id: 'customer-acknowledgment',
      titleKey: 'settings:administration.tools.customerAcknowledgment.title',
      descriptionKey:
        'settings:administration.tools.customerAcknowledgment.description',
      icon: <Description />,
      path: '/settings/administration/customer-acknowledgment',
      available: true
    },
    {
      id: 'general-settings',
      titleKey: 'settings:administration.tools.generalSettings.title',
      descriptionKey:
        'settings:administration.tools.generalSettings.description',
      icon: <Tune />,
      path: '/settings/administration/general-settings',
      available: true
    },
    {
      id: 'station-devices',
      titleKey: 'settings:administration.tools.stationDevices.title',
      descriptionKey:
        'settings:administration.tools.stationDevices.description',
      icon: <Router />,
      path: '/settings/administration/station-devices',
      available: true
    },
    {
      id: 'action-commands',
      titleKey: 'settings:administration.tools.actionCommands.title',
      descriptionKey:
        'settings:administration.tools.actionCommands.description',
      icon: <Terminal />,
      path: '/settings/administration/action-commands',
      available: true
    },
    {
      id: 'system-config',
      titleKey: 'settings:administration.tools.systemConfig.title',
      descriptionKey: 'settings:administration.tools.systemConfig.description',
      icon: <Settings />,
      path: '/settings/administration/system-config',
      available: false
    }
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 600,
            mb: 1,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            WebkitTextFillColor: 'transparent'
          }}
        >
          {t('settings:administration.dashboard.title')}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ fontSize: '1.1rem', maxWidth: 600 }}
        >
          {t('settings:administration.dashboard.description')}
        </Typography>
      </Box>

      <Grid
        container
        spacing={3}
        sx={{ display: 'flex', alignItems: 'stretch' }}
      >
        {adminTools.map(tool => (
          <Grid item xs={12} md={6} key={tool.id} sx={{ display: 'flex' }}>
            <Card
              onClick={() => tool.available && navigate(tool.path)}
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: tool.available ? 'pointer' : 'not-allowed',
                position: 'relative',
                border: `1px solid ${
                  tool.available
                    ? alpha(theme.palette.primary.main, 0.12)
                    : alpha(theme.palette.text.secondary, 0.08)
                }`,
                background: tool.available
                  ? theme.palette.background.paper
                  : `linear-gradient(135deg, ${alpha(theme.palette.action.disabledBackground, 0.02)} 0%, ${alpha(theme.palette.action.disabledBackground, 0.05)} 100%)`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: tool.available
                  ? '0 2px 4px 0 rgba(0,0,0,0.05)'
                  : 'none',
                '&:hover': tool.available
                  ? {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 20px 0 rgba(0,0,0,0.08)',
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.01)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`
                    }
                  : {
                      background: `linear-gradient(135deg, ${alpha(theme.palette.action.disabledBackground, 0.03)} 0%, ${alpha(theme.palette.action.disabledBackground, 0.06)} 100%)`
                    }
              }}
            >
              <CardContent
                sx={{
                  p: 4,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  position: 'relative'
                }}
              >
                {!tool.available && (
                  <Chip
                    label={t('settings:administration.dashboard.comingSoon')}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      backgroundColor: alpha(theme.palette.warning.main, 0.1),
                      color: theme.palette.warning.dark,
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      height: 24,
                      borderRadius: 12
                    }}
                  />
                )}

                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    background: tool.available
                      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`
                      : alpha(theme.palette.action.disabledBackground, 0.3),
                    transition: 'all 0.3s',
                    ...(tool.available && {
                      '&:hover': {
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`
                      }
                    })
                  }}
                >
                  {cloneElement(tool.icon, {
                    sx: {
                      fontSize: 48,
                      color: tool.available
                        ? theme.palette.primary.main
                        : theme.palette.text.disabled,
                      transition: 'all 0.3s'
                    }
                  })}
                </Box>

                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    color: tool.available
                      ? theme.palette.text.primary
                      : theme.palette.text.secondary,
                    mb: 2
                  }}
                >
                  {t(tool.titleKey)}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: tool.available
                      ? theme.palette.text.secondary
                      : theme.palette.text.disabled,
                    lineHeight: 1.6,
                    minHeight: 48
                  }}
                >
                  {t(tool.descriptionKey)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
